import { useEffect, useMemo, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Checkbox, message, Form, Input, Space, Tooltip, Divider } from 'antd';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';

const { Search } = Input;
import PageHeader from '@components/PageHeader/PageHeader';
import { workspaceSelectionVoteCasesPath, workspaceSelectionVotePrivilegedUsersPath, workspaceSelectionVoteSettingsPath } from '@/layouts/selectionWorkspacePaths';

import { getProjestsApi, fetchCasevotetopicsPage, fetchCandidateTopics, saveCandidateTopics, deleteCasevotetopics } from '@services/Selection';

import './index.less';
const { confirm } = Modal;

const getColumns = ({ params, navigate, removeTopic }) => [
    {
        title: '投票主题',
        dataIndex: 'name',
    },
    {
        title: '投票起止时间',
        key: 'id',
        align: 'center',
        width: 280,
        render: row => {
            return row?.topicVoteSettings?.startedAt ? row.topicVoteSettings.startedAt + '～' + row.topicVoteSettings.endedAt : '-';
        },
    },
    {
        title: '实践票数',
        key: 'id',
        align: 'center',
        width: 80,
        render: row => {
            if (!row.topicVoteSettings) return '-';
            return row?.topicVoteSettings?.visibleVoteNum ? '展示' : '不展示';
        },
    },
    {
        title: '投票按钮',
        key: 'id',
        align: 'center',
        width: 80,
        render: row => {
            if (!row.topicVoteSettings) return '-';
            return row?.topicVoteSettings?.visibleVoteButton ? '展示' : '不展示';
        },
    },
    {
        title: '投票排行榜',
        key: 'id',
        align: 'center',
        width: 100,
        render: row => {
            if (!row.topicVoteSettings) return '-';
            return row?.topicVoteSettings?.visibleRank ? '展示' : '不展示';
        },
    },
    {
        title: '操作',
        key: 'id',
        align: 'center',
        width: 240,
        render: row => {
            return (
                <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                    <Button type="link" size="small" onClick={() => navigate(workspaceSelectionVoteSettingsPath(params.pid, row.id), { state: row.topicVoteSettings })}>
                        投票规则
                    </Button>
                    <Button type="link" size="small" onClick={() => navigate(workspaceSelectionVotePrivilegedUsersPath(params.pid, row.topicId))}>
                        特权用户
                    </Button>
                    <Button type="link" size="small" onClick={() => navigate(workspaceSelectionVoteCasesPath(params.pid, row.topicId))}>
                        投票实践
                    </Button>
                    <Button type="link" size="small" onClick={() => removeTopic(row.id)}>
                        移除投票主题
                    </Button>
                </Space>
            );
        },
    },
];

const ScoreStageTopicList = () => {
    const params = useParams();
    const [form] = Form.useForm();
    const [dataSource, setDataSource] = useState([]);
    const [topicSource, setTopicSource] = useState([]);
    const [topicModal, setTopicModal] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [topicName, setTopicName] = useState('');
    const [projectInfo, setProjectInfo] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const navigate = useNavigate();

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const fetchList = useCallback(
        (name, needLoading = true) => {
            if (needLoading) {
                setTableLoading(true);
            }
            fetchCasevotetopicsPage({ projectId: params.pid, topicName: name })
                .then(result => {
                    if (result?.status_code === 200) {
                        setDataSource(result.data?.map(item => ({ ...item, topicVoteSettings: !!item.topicVoteSettings && typeof item.topicVoteSettings === 'string' ? JSON.parse(item.topicVoteSettings) : null })) || []);
                    }
                })
                .finally(() => {
                    setTableLoading(false);
                });
        },
        [params],
    );

    useEffect(() => {
        fetchList();
        fetchCandidateTopics({ projectId: params.pid }).then(result => {
            if (result?.status_code === 200) {
                setTopicSource(result.data?.map(i => ({ label: i.name, value: i.id })) || []);
            }
        });
    }, [fetchList, params]);

    // 获取项目基本信息用于 PageHeader 展示
    useEffect(() => {
        if (params.pid && !projectInfo) {
            getProjestsApi({ id: params.pid }).then(res => {
                if (res.status_code === 200) {
                    setProjectInfo(res.data);
                }
            });
        }
    }, [params.pid]);

    useEffect(() => {
        document.title = '投票管理-医评议';
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const handleRefresh = () => fetchList(topicName);

    const columns = useMemo(() => {
        const removeTopic = id => {
            confirm({
                icon: <i className="bi bi-exclamation-circle"></i>,
                content: '确定移除所选主题？',
                centered: true,
                mask: { closable: true },
                onOk() {
                    deleteCasevotetopics({ id }).then(res => {
                        if (res.status_code === 200) {
                            message.success('移除成功');
                            fetchList();
                            setTopicName('');
                        }
                    });
                },
            });
        };

        return getColumns({ params, navigate, removeTopic });
    }, [fetchList, navigate, params]);

    const handleAddTopic = () => {
        const newSource = [...topicSource].filter(d => !dataSource.some(i => i.topicId === d.value));
        setTopicSource(newSource);
        if (!newSource.length) {
            return message.warning('无待添加投票主题！');
        }
        setTopicModal(true);
    };

    const onSearchTopicName = value => {
        const next = value ?? '';
        setTopicName(next);
        fetchList(next);
    };

    const onOk = () => {
        const topicIds = form.getFieldValue('topics');

        if (topicIds?.length) {
            setConfirmLoading(true);
            saveCandidateTopics({ projectId: params.pid, topicIds })
                .then(res => {
                    if (res?.status_code === 200) {
                        fetchList();
                        message.success('添加成功！');
                        setTopicModal(false);
                        form.resetFields();
                    } else {
                        message.warning('添加失败，请重试！');
                    }
                })
                .finally(() => {
                    setConfirmLoading(false);
                });
        } else {
            message.warning('请选择要添加的投票主题！');
        }
    };

    return (
        <div className="page-container list-page-container vote-management-page">
            <PageHeader title="投票管理" breadcrumb={[{ label: '医评议', path: '/selection/projects' }, { label: projectInfo?.name }, { label: '投票管理' }]} />

            <div ref={tableRegionRef} className="table-region-container vote-topic-list-table-region">
                <div className="main-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" onClick={handleAddTopic}>
                                添加投票主题
                            </Button>
                            <Search placeholder="主题名称" allowClear style={{ width: 250 }} value={topicName} onChange={e => setTopicName(e.target.value)} onSearch={onSearchTopicName} />
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={handleRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap vote-topic-list-table-wrap">
                    <Table className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined} dataSource={dataSource} columns={columns} rowKey="id" bordered size="medium" pagination={false} loading={tableLoading} scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }} />
                </div>
            </div>

            <Modal
                centered
                width={600}
                title="选择评议主题"
                open={topicModal}
                confirmLoading={confirmLoading}
                onOk={onOk}
                onCancel={() => setTopicModal(false)}
                afterClose={() => {
                    setTopicModal(false);
                    form.resetFields();
                }}
            >
                <Form form={form}>
                    <Form.Item
                        name="topics"
                        label="评议主题"
                        labelCol={{
                            sm: { span: 8 },
                        }}
                        wrapperCol={{
                            sm: { offset: 1 },
                        }}
                    >
                        <Checkbox.Group options={topicSource} className="pingwei-checkgroup" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ScoreStageTopicList;
